import { jsPDF } from "jspdf";

const getBase64ImageFromUrl = (imageUrl) => {
  return new Promise((resolve) => {
    if (!imageUrl) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (err) {
        console.error("Canvas draw failed:", err);
        resolve(null);
      }
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = imageUrl;
  });
};

export const generateReceiptPDF = async (txInfo, logoUrl) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Fetch logo base64 if url is provided
  let logoBase64 = null;
  if (logoUrl) {
    logoBase64 = await getBase64ImageFromUrl(logoUrl);
  }

  const pageWidth = 210;
  const pageHeight = 297;
  const marginX = 15; // Set wider margins (15mm instead of 20mm)

  // Draw center watermark if logo is available
  if (logoBase64) {
    try {
      doc.saveGraphicsState();
      const gState = new doc.GState({ opacity: 0.05 });
      doc.setGState(gState);
      doc.addImage(
        logoBase64,
        "PNG",
        (pageWidth - 120) / 2,
        (pageHeight - 45) / 2,
        120,
        45,
        undefined,
        "FAST",
      );
      doc.restoreGraphicsState();
    } catch (e) {
      console.error("Failed to add center watermark:", e);
    }
  }

  // Header Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(17, 24, 39); // Gray 900
  doc.text("Receipt", marginX, 25);

  // Logo in top right
  if (logoBase64) {
    try {
      doc.addImage(
        logoBase64,
        "PNG",
        pageWidth - marginX - 60,
        12,
        60,
        15,
        undefined,
        "FAST",
      );
    } catch (e) {
      console.error("Error adding logo image to PDF:", e);
      // Text fallback
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(46, 58, 140);
      doc.text("VheecO", pageWidth - marginX - 35, 22);
    }
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(46, 58, 140);
    doc.text("VheecO", pageWidth - marginX - 35, 22);
  }

  // Invoice Details metadata (left-aligned)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(17, 24, 39);

  let currentY = 40;
  const rowHeight = 5; // slightly larger row spacing for premium look
  const labelColX = marginX;
  const valueColX = marginX + 35; // Wider label-to-value gap

  // Receipt Number
  doc.setFont("helvetica", "bold");
  doc.text("Receipt number", labelColX, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(txInfo.invoiceNumber || txInfo.receiptId, valueColX, currentY);
  currentY += rowHeight;

  // Transaction id Number
  doc.setFont("helvetica", "bold");
  doc.text("Transaction Id", labelColX, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(txInfo.receiptId, valueColX, currentY);
  currentY += rowHeight;

  // Date Paid
  doc.setFont("helvetica", "bold");
  doc.text("Date paid", labelColX, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(txInfo.date, valueColX, currentY);
  currentY += rowHeight;

  // Billing Period
  doc.setFont("helvetica", "bold");
  doc.text("Billing period", labelColX, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(txInfo.billingPeriod || "One-time transfer", valueColX, currentY);
  currentY += rowHeight;

  currentY += 12;

  // Two columns for Address details: Left (Sender), Right (Receiver / Bill to)
  const leftColX = marginX;
  const rightColX = pageWidth / 2 + 10; // wider gap between columns

  let addressY = currentY;

  // Left column: Sender info
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.text("Sender Details", leftColX, addressY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(75, 85, 99); // Gray 600
  addressY += 6;
  doc.text(txInfo.sender.name || "N/A", leftColX, addressY);
  addressY += rowHeight;
  doc.text(`ID: ${txInfo.sender.entityId || "N/A"}`, leftColX, addressY);
  if (txInfo.sender.email) {
    addressY += rowHeight;
    doc.text(`Email: ${txInfo.sender.email}`, leftColX, addressY);
  }
  if (txInfo.sender.phone) {
    addressY += rowHeight;
    doc.text(`Phone: ${txInfo.sender.phone}`, leftColX, addressY);
  }
  addressY += rowHeight;
  doc.text(
    `Role: ${(txInfo.sender.role || "N/A").toUpperCase()}`,
    leftColX,
    addressY,
  );

  // Right column: Receiver info (Bill to)
  let rightAddressY = currentY;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.text("Bill to (Receiver)", rightColX, rightAddressY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(75, 85, 99); // Gray 600
  rightAddressY += 6;
  doc.text(txInfo.receiver.name || "N/A", rightColX, rightAddressY);
  rightAddressY += rowHeight;
  doc.text(
    `ID: ${txInfo.receiver.entityId || "N/A"}`,
    rightColX,
    rightAddressY,
  );
  if (txInfo.receiver.email) {
    rightAddressY += rowHeight;
    doc.text(`Email: ${txInfo.receiver.email}`, rightColX, rightAddressY);
  }
  if (txInfo.receiver.phone) {
    rightAddressY += rowHeight;
    doc.text(`Phone: ${txInfo.receiver.phone}`, rightColX, rightAddressY);
  }

  currentY = Math.max(addressY, rightAddressY) + 15;

  // Headline: "Amount paid on Date" (no money sign)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(17, 24, 39);
  doc.text(
    `${txInfo.amount.toFixed(2)} paid on ${txInfo.date}`,
    marginX,
    currentY,
  );
  currentY += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(75, 85, 99);
  doc.text(
    `Transfer description: ${txInfo.note || "Wallet Balance Credit"}`,
    marginX,
    currentY,
  );
  currentY += 10;

  // Table Headers (Wider table spacing)
  doc.setDrawColor(229, 231, 235); // Gray 200
  doc.setLineWidth(0.3);
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 4.5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(75, 85, 99);

  const colDescX = marginX;
  const colQtyX = pageWidth - marginX - 60;
  const colPriceX = pageWidth - marginX - 30;
  const colAmountX = pageWidth - marginX;

  doc.text("Description", colDescX, currentY);
  doc.text("Qty", colQtyX, currentY, { align: "right" });
  doc.text("Unit price", colPriceX, currentY, { align: "right" });
  doc.text("Amount", colAmountX, currentY, { align: "right" });

  currentY += 2.5;
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 6;

  // Table Row (no money sign)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(17, 24, 39);

  const descText = `Wallet Balance Credit to ${txInfo.receiver.name} (${txInfo.receiver.entityId})`;
  doc.text(descText, colDescX, currentY);
  doc.text("1", colQtyX, currentY, { align: "right" });
  doc.text(`${txInfo.amount.toFixed(2)}`, colPriceX, currentY, {
    align: "right",
  });
  doc.text(`${txInfo.amount.toFixed(2)}`, colAmountX, currentY, {
    align: "right",
  });

  currentY += 5;
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 6;

  // Subtotal / Totals Block (no money sign)
  const rightBlockX = pageWidth - marginX - 60;
  const rightBlockValX = pageWidth - marginX;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(17, 24, 39);
  doc.text("Subtotal", rightBlockX, currentY);
  doc.text(`${txInfo.amount.toFixed(2)}`, rightBlockValX, currentY, {
    align: "right",
  });
  currentY += 4.5;

  // doc.text("Total", rightBlockX, currentY);
  // doc.text(`${txInfo.amount.toFixed(2)}`, rightBlockValX, currentY, {
  //   align: "right",
  // });
  // currentY += 4.5;

  // doc.setFont("helvetica", "bold");
  // doc.setTextColor(17, 24, 39);
  // doc.text("Amount paid", rightBlockX, currentY);
  // doc.text(`${txInfo.amount.toFixed(2)}`, rightBlockValX, currentY, {
  //   align: "right",
  // });

  currentY += 15;

  // Payment History Section
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Payment history", marginX, currentY);
  currentY += 6;

  // Payment History Table Headers
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 4.5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(75, 85, 99);

  const histMethodX = marginX;
  const histDateX = marginX + 80;
  const histAmountX = pageWidth - marginX - 50;
  const histReceiptX = pageWidth - marginX;

  doc.text("Payment method", histMethodX, currentY);
  doc.text("Date", histDateX, currentY);
  doc.text("Amount paid", histAmountX, currentY, { align: "right" });
  doc.text("Transaction Id", histReceiptX, currentY, { align: "right" });

  currentY += 2.5;
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 6;

  // Payment History Row (no money sign)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(17, 24, 39);

  doc.text("Wallet Load", histMethodX, currentY);
  doc.text(txInfo.date, histDateX, currentY);
  doc.text(`${txInfo.amount.toFixed(2)}`, histAmountX, currentY, {
    align: "right",
  });
  doc.text(txInfo.receiptId, histReceiptX, currentY, { align: "right" });

  currentY += 4.5;
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 12;

  // Support Footer text
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(75, 85, 99);
  doc.text(
    "See contact support or log in to your wallet for usage details.",
    marginX,
    currentY,
  );

  // Page numbering
  doc.text("Page 1 of 1", pageWidth - marginX, pageHeight - 15, {
    align: "right",
  });

  // Trigger PDF Download
  doc.save(`receipt-${txInfo.receiptId}.pdf`);
};
